import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, FlaskConical, Loader2, Trash2, Pencil, Factory, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadPdf, fileUrl } from "@/lib/files";

const fmtQty = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 4 });
const fmtInt = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 });

export default function Recettes() {
  const { societes, societeParam, societeId } = useApp();
  const { can } = useAuth();
  const canEdit = can("recettes");
  const [recettes, setRecettes] = useState([]);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);

  const sid = () => (societeId !== "all" ? societeId : societes[0]?.id || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/recettes", { params: societeParam });
      setRecettes(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [societeId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const loadProduits = (s) => { if (s) api.get("/produits", { params: { societe_id: s } }).then((r) => setProduits(r.data)).catch(() => {}); };

  const openNew = () => {
    const s = sid();
    setForm({ id: null, societe_id: s, nom: "", produit_fini_id: "", rendement: 0, unite_rendement: "pièce",
      ingredients: [{ nom: "", quantite: 0, unite: "kg" }], notes: "" });
    loadProduits(s);
    setOpen(true);
  };
  const openEdit = (r) => {
    setForm({ id: r.id, societe_id: r.societe_id, nom: r.nom, produit_fini_id: r.produit_fini_id || "", rendement: r.rendement,
      unite_rendement: r.unite_rendement || "pièce",
      ingredients: r.ingredients.length ? r.ingredients.map((i) => ({ ...i })) : [{ nom: "", quantite: 0, unite: "kg" }],
      notes: r.notes || "" });
    loadProduits(r.societe_id);
    setOpen(true);
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setIng = (i, k, v) => setForm((f) => { const c = [...f.ingredients]; c[i] = { ...c[i], [k]: v }; return { ...f, ingredients: c }; });
  const setIngProduct = (i, pid) => setForm((f) => { const c = [...f.ingredients]; const p = produits.find((x) => x.id === pid); c[i] = { ...c[i], produit_id: pid, nom: p ? p.nom : c[i].nom, unite: (p && p.unite) || c[i].unite }; return { ...f, ingredients: c }; });
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { nom: "", quantite: 0, unite: "kg" }] }));
  const rmIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.nom.trim()) return toast.error("Nom de la fiche technique requis.");
    const ings = form.ingredients.filter((i) => i.nom.trim());
    if (ings.length === 0) return toast.error("Ajoutez au moins un ingrédient.");
    setSaving(true);
    try {
      const payload = { societe_id: form.societe_id, nom: form.nom.trim(),
        produit_fini_id: form.produit_fini_id || null,
        rendement: Number(form.rendement) || 0, unite_rendement: form.unite_rendement || "pièce",
        ingredients: ings.map((i) => ({ nom: i.nom.trim(), quantite: Number(i.quantite) || 0, unite: i.unite || "kg", produit_id: i.produit_id || null })),
        notes: form.notes || "" };
      if (form.id) await api.put(`/recettes/${form.id}`, payload);
      else await api.post("/recettes", payload);
      toast.success(form.id ? "Fiche technique modifiée." : "Fiche technique créée.");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await api.delete(`/recettes/${delId}`); toast.success("Fiche technique supprimée."); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Fiche technique" subtitle="Nomenclatures de fabrication : ingrédients et quantités par unité" testid="recettes-page"
        actions={<div className="flex gap-2">
          {recettes.length > 0 && <Button variant="outline" onClick={() => downloadPdf(`/pdf/fiches-techniques?societe_id=${sid()}`, "fiches-techniques")} className="gap-2" data-testid="fiches-pdf-btn"><FileDown className="h-4 w-4" /> Fiches (PDF)</Button>}
          {canEdit && <Button onClick={openNew} className="gap-2" data-testid="new-recette-btn"><Plus className="h-4 w-4" /> Nouvelle fiche technique</Button>}
        </div>} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : recettes.length === 0 ? <div className="rounded-xl border bg-card"><EmptyState icon={FlaskConical} title="Aucune fiche technique" hint="Créez une fiche technique : définissez les ingrédients et leur quantité par unité." /></div>
        : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {recettes.map((r) => (
              <div key={r.id} className="rounded-xl border bg-card p-5" data-testid={`recette-card-${r.id}`}>
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-start gap-3">
                    {r.produit_fini_photo_id && <img src={fileUrl(r.produit_fini_photo_id)} alt={r.produit_fini_name || r.nom} className="h-14 w-14 rounded-lg object-cover border shrink-0" data-testid={`recette-photo-${r.id}`} />}
                    <div>
                      <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Factory className="h-4 w-4 text-primary" /> {r.nom}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Production totale : <span className="tabular font-medium text-foreground">{fmtInt(r.rendement)}</span> {r.unite_rendement}</p>
                      {r.produit_fini_name && <p className="text-xs text-muted-foreground mt-0.5">Produit fini : <span className="font-medium text-foreground">{r.produit_fini_name}</span>{r.produit_fini_reference ? <span className="tabular"> (réf. {r.produit_fini_reference})</span> : ""}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => downloadPdf(`/pdf/fiche-technique/${r.id}`, `fiche-${r.nom}`)} className="p-1.5 text-muted-foreground hover:text-primary" data-testid={`recette-pdf-${r.id}`} title="Télécharger la fiche technique (PDF)"><FileDown className="h-4 w-4" /></button>
                    {canEdit && <>
                      <button onClick={() => openEdit(r)} className="p-1.5 text-muted-foreground hover:text-primary" data-testid={`recette-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => setDelId(r.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`recette-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </>}
                  </div>
                </div>
                <div className="overflow-x-auto mt-3">
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50"><TableHead>Ingrédient</TableHead><TableHead className="text-right">Qté / pièce</TableHead><TableHead className="text-right">Total production</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {r.ingredients.map((ing, i) => (
                        <TableRow key={i} data-testid={`recette-${r.id}-ing-${i}`}>
                          <TableCell className="text-sm">{ing.nom}</TableCell>
                          <TableCell className="tabular text-right text-sm">{fmtQty(ing.quantite)} {ing.unite}</TableCell>
                          <TableCell className="tabular text-right text-sm text-muted-foreground">{fmtInt(ing.quantite * r.rendement)} {ing.unite}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {r.notes && <p className="text-[11px] text-muted-foreground mt-2 italic">{r.notes}</p>}
              </div>
            ))}
          </div>
          </>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="recette-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{form?.id ? "Modifier la fiche technique" : "Nouvelle fiche technique"}</DialogTitle><DialogDescription>Définissez le nom, la production totale et les ingrédients (quantité par unité).</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5 sm:col-span-2"><Label>Nom de la fiche technique / produit *</Label><Input value={form.nom} onChange={(e) => setField("nom", e.target.value)} data-testid="recette-nom" placeholder="Ex. Beignet" /></div>
                <div className="space-y-1.5"><Label>Production totale</Label><Input type="number" value={form.rendement} onChange={(e) => setField("rendement", e.target.value)} data-testid="recette-rendement" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Produit fini (optionnel — pour la consommation auto en Production)</Label>
                <Select value={form.produit_fini_id || "none"} onValueChange={(v) => { const id = v === "none" ? "" : v; setField("produit_fini_id", id); const p = produits.find((x) => x.id === id); if (p && !form.nom.trim()) setField("nom", p.nom); }}>
                  <SelectTrigger data-testid="recette-produit-fini"><SelectValue placeholder="Aucun produit fini lié" /></SelectTrigger>
                  <SelectContent className="bg-popover max-h-64">
                    <SelectItem value="none">Aucun</SelectItem>
                    {produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}{p.reference ? ` (réf. ${p.reference})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label>Ingrédients (produit du stock · quantité par pièce)</Label>
              </div>
              <div className="space-y-2">
                {form.ingredients.map((ing, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Select value={ing.produit_id || undefined} onValueChange={(v) => setIngProduct(i, v)}>
                      <SelectTrigger className="flex-1" data-testid={`recette-ing-prod-${i}`}><SelectValue placeholder="Choisir un produit du stock" /></SelectTrigger>
                      <SelectContent className="bg-popover max-h-64">
                        {ing.produit_id && !produits.some((p) => p.id === ing.produit_id) && <SelectItem value={ing.produit_id}>{ing.nom}</SelectItem>}
                        {produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}{p.reference ? ` (${p.reference})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="w-24 text-right" type="number" step="any" value={ing.quantite} onChange={(e) => setIng(i, "quantite", e.target.value)} data-testid={`recette-ing-qty-${i}`} />
                    <Input className="w-16" value={ing.unite} onChange={(e) => setIng(i, "unite", e.target.value)} placeholder="kg" data-testid={`recette-ing-unite-${i}`} />
                    <button className="text-muted-foreground hover:text-destructive disabled:opacity-40" onClick={() => rmIng(i)} disabled={form.ingredients.length === 1} data-testid={`recette-ing-del-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={addIng} className="text-sm text-primary flex items-center gap-1.5" data-testid="recette-add-ing"><Plus className="h-4 w-4" /> Ajouter un ingrédient</button>
                <p className="text-[11px] text-muted-foreground">Les ingrédients proviennent de vos produits du Stock (matières premières). Le coût se calcule à partir de leur prix d'achat.</p>
              </div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} data-testid="recette-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="recette-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent data-testid="recette-del-dialog">
          <AlertDialogHeader><AlertDialogTitle>Supprimer cette fiche technique ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={doDelete} data-testid="recette-del-confirm">Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
